import { DailyResponse, HourlyResponse, Resource } from './types'
import { useFormat, useT } from './i18n'
import type { Fmt } from './format'
import type { T } from './i18n'

type Kpi = { label: string; value: string; hint?: string }

type Props = {
  daily: Resource<DailyResponse>
  hourly: Resource<HourlyResponse>
}

function buildKpis(daily: DailyResponse, hourly: Resource<HourlyResponse>, t: T, f: Fmt): Kpi[] {
  const k = daily.kpis
  const hi = daily.extremes.highest
  const lo = daily.extremes.lowest
  const peak = hourly.status === 'ready' ? hourly.data.peak : null
  return [
    {
      label: t('PROFILES_DASHBOARD.KPI.TOTAL'),
      value: f.kwh(k.total),
      hint: t('PROFILES_DASHBOARD.KPI.TOTAL_HINT', { count: k.complete_days }),
    },
    { label: t('PROFILES_DASHBOARD.KPI.AVERAGE'), value: f.kwh(k.average_per_day) },
    {
      label: t('PROFILES_DASHBOARD.KPI.HIGHEST'),
      value: f.kwh(hi?.total),
      hint: hi ? f.date(hi.date) : undefined,
    },
    {
      label: t('PROFILES_DASHBOARD.KPI.LOWEST'),
      value: f.kwh(lo?.total),
      hint: lo ? f.date(lo.date) : undefined,
    },
    {
      label: t('PROFILES_DASHBOARD.KPI.PEAK'),
      value:
        hourly.status === 'ready' ? f.kwh(peak?.value, 1) : hourly.status === 'error' ? '—' : '…',
      hint: peak ? `${f.date(peak.date)} · ${peak.slot}` : undefined,
    },
    {
      label: t('PROFILES_DASHBOARD.KPI.UPLIFT'),
      value: f.percent(k.uplift_pct),
      hint: k.event_days
        ? t('PROFILES_DASHBOARD.KPI.UPLIFT_HINT', { count: k.event_days })
        : t('PROFILES_DASHBOARD.KPI.UPLIFT_NONE'),
    },
  ]
}

export function KpiCards({ daily, hourly }: Props) {
  const t = useT()
  const f = useFormat()
  if (daily.status === 'error') {
    return (
      <div className='alert alert-danger mb-5' role='alert'>
        {daily.message}
      </div>
    )
  }
  if (daily.status !== 'ready') {
    return (
      <div className='row g-4 mb-5' aria-busy='true'>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div className='col-6 col-md-4 col-xl-2' key={i}>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body' style={{ minHeight: 98 }}>
                <span className='text-muted fs-7'>
                  {daily.status === 'loading' ? t('PROFILES_DASHBOARD.LOADING') : ''}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }
  const { incomplete_days, complete_days } = daily.data.kpis
  return (
    <>
      <div className='row g-4 mb-2'>
        {buildKpis(daily.data, hourly, t, f).map((k) => (
          <div className='col-6 col-md-4 col-xl-2' key={k.label}>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='fs-7 text-gray-500 fw-semibold'>{k.label}</div>
                <div className='fs-2 fw-bold text-gray-900'>{k.value}</div>
                <div className='fs-8 text-muted' style={{ minHeight: 18 }}>
                  {k.hint || ''}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className='fs-8 text-muted mb-5' data-testid='data-quality-note'>
        {incomplete_days > 0
          ? t('PROFILES_DASHBOARD.KPI.NOTE_PARTIAL', {
              complete: complete_days,
              incomplete: incomplete_days,
            })
          : t('PROFILES_DASHBOARD.KPI.NOTE_OK', { complete: complete_days })}
      </div>
    </>
  )
}
